import type { LucideIcon } from 'lucide-react';
import {
  Bell,
  BookOpen,
  CalendarCheck,
  FolderOpen,
  HelpCircle,
  Home,
  PencilLine,
  Plane,
  Settings,
  User,
} from 'lucide-react';

/**
 * Navigation conditional logic — single source of truth (PRD §6.3).
 *
 * Every surface (desktop sidebar, mobile bottom bar, mobile drawer, header
 * avatar dropdown) renders from this registry. Items whose route hasn't
 * shipped yet (P2 backlog) are declared with `enabled: false` so the §6.3
 * rule set is complete — flip the flag when the route lands, nothing else
 * changes.
 */

export type NavSurface = 'sidebar' | 'bottom' | 'drawer' | 'dropdown';

export interface NavContext {
  /** applicant.arrived / on-program lifecycle (drives Check-ins & SEVIS). */
  onProgram: boolean;
}

export interface NavItem {
  key: string;
  label: string;
  to: string;
  Icon: LucideIcon;
  /** Route exists in PPPToo today. P2 slots ship disabled. */
  enabled: boolean;
  /** §6.3 display condition. */
  show: (n: NavContext) => boolean;
  surfaces: NavSurface[];
}

/** Statuses treated as on-program (AP-16 status-driven navigation). */
export const ON_PROGRAM_STATUSES = ['on_program', 'active', 'arrived', 'in_country'];

export const isOnProgram = (status?: string | null) =>
  ON_PROGRAM_STATUSES.includes((status ?? '').toLowerCase());

const always = () => true;

export const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', to: '/dashboard', Icon: Home, enabled: true, show: always, surfaces: ['sidebar', 'bottom', 'drawer'] },
  { key: 'application', label: 'My Application', to: '/application', Icon: PencilLine, enabled: true, show: always, surfaces: ['sidebar', 'bottom', 'drawer'] },
  { key: 'documents', label: 'My Documents', to: '/documents', Icon: FolderOpen, enabled: true, show: always, surfaces: ['sidebar', 'drawer'] },
  { key: 'profile', label: 'My Profile', to: '/profile', Icon: User, enabled: true, show: always, surfaces: ['drawer', 'dropdown'] },
  { key: 'settings', label: 'Settings', to: '/settings', Icon: Settings, enabled: true, show: always, surfaces: ['drawer', 'dropdown'] },
  // AP-13: notifications inbox — enabled, surfaces drawer + dropdown (bell in header handles primary access)
  { key: 'notifications', label: 'My Notifications', to: '/notifications', Icon: Bell, enabled: true, show: always, surfaces: ['drawer', 'dropdown'] },
  // ---- Sections deferred beyond release 1 (⛔ awaken by flipping `enabled`) ----
  // Check-ins has a working route but is OUT of release 1. When it ships it joins
  // the hamburger drawer (NOT the bottom bar) per the primary-tabs rule below.
  { key: 'checkins', label: 'My Check-ins', to: '/check-ins', Icon: CalendarCheck, enabled: false, show: (n) => n.onProgram, surfaces: ['drawer'] },
  { key: 'sevis', label: 'SEVIS Check-in', to: '/sevis-check-in', Icon: Plane, enabled: false, show: (n) => n.onProgram, surfaces: ['sidebar', 'drawer'] },
  { key: 'resources', label: 'Program Resources', to: '/resources', Icon: BookOpen, enabled: false, show: always, surfaces: ['sidebar', 'drawer'] },
  { key: 'help', label: 'Help & Support', to: '/help', Icon: HelpCircle, enabled: false, show: always, surfaces: ['sidebar', 'drawer', 'dropdown'] },
];

/**
 * Items for one surface, in registry order.
 *
 * Mobile bottom bar (PRD §6.4): reserved for the PRIMARY tabs only — in
 * release 1 that's Dashboard + Application. Everything else (Documents,
 * Profile, Settings, and future sections such as Check-ins) lives in the
 * hamburger drawer. New sections default to the drawer: only add 'bottom' to
 * an item's surfaces when it should be promoted to a primary tab. The bar is
 * still capped at 4 as a safety rail.
 */
export function navFor(surface: NavSurface, n: NavContext): NavItem[] {
  const items = NAV_ITEMS.filter((i) => i.enabled && i.surfaces.includes(surface) && i.show(n));
  return surface === 'bottom' ? items.slice(0, 4) : items;
}
